/**
 * BFF Auth Service
 *
 * Authentication via BFF with HttpOnly session cookies. Tokens never touch the browser.
 * All auth operations go through the BFF; credentials are sent with every request.
 * Returns snake_case User to match tRPC/DB contract.
 */

import { env } from "../../config/env";
import type { User } from "../../types";

/** Normalize BFF user response to snake_case User (no conversion; pass-through) */
function toUser(data: Record<string, unknown>): User {
	let role: User["role"] = "partner";
	if (data.role === "admin") role = "admin";
	else if (data.role === "stellar_admin") role = "stellar_admin";
	return {
		id: String(data.id ?? ""),
		email: String(data.email ?? ""),
		name: String(data.name ?? ""),
		role,
		wallet_ids: Array.isArray(data.wallet_ids)
			? data.wallet_ids.map(String)
			: [],
		created_at: String(data.created_at ?? new Date().toISOString()),
		last_login: data.last_login != null ? String(data.last_login) : undefined,
	};
}

/** Fetch with credentials for cookie-based auth */
async function apiFetch(
	path: string,
	options: RequestInit = {},
): Promise<Response> {
	// nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf -- env.API_URL is build-time config; `path` is a static internal API route, not user input
	return fetch(`${env.API_URL}${path}`, {
		...options,
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
			...options.headers,
		},
	});
}

/** Parse response as JSON or throw clear error (handles HTML from proxy replacing 4xx) */
async function parseJsonOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	try {
		return JSON.parse(text) as T;
	} catch {
		throw new Error("Unexpected response from server. Please try again.");
	}
}

export class NewPasswordRequiredError extends Error {
	constructor() {
		super("New password required");
		this.name = "NewPasswordRequiredError";
	}
}

class BFFAuthService {
	async login(email: string, password: string): Promise<User> {
		const res = await apiFetch("/api/auth/login", {
			method: "POST",
			body: JSON.stringify({ email, password }),
		});

		const data = await parseJsonOrThrow<{
			success?: boolean;
			error?: string;
			errorCode?: string;
			data?: Record<string, unknown>;
		}>(res);

		if (data?.errorCode === "NEW_PASSWORD_REQUIRED") {
			throw new NewPasswordRequiredError();
		}

		if (!res.ok || !data.success || !data.data) {
			throw new Error(data?.error ?? "Login failed");
		}

		const user = toUser(data.data);
		// Sync user with BFF (creates/updates in Couchbase, returns full user with id + wallet_ids)
		try {
			const syncRes = await apiFetch("/api/users/syncs", {
				method: "POST",
				body: JSON.stringify({
					email: user.email,
					name: user.name,
					role: user.role,
				}),
			});
			const syncData = await parseJsonOrThrow<{
				success?: boolean;
				data?: Record<string, unknown>;
			}>(syncRes);
			if (syncRes.ok && syncData.success && syncData.data) {
				return toUser(syncData.data);
			}
		} catch {
			// Sync failed — use login response (may lack wallet_ids)
		}
		return user;
	}

	async completeNewPassword(newPassword: string): Promise<User> {
		const res = await apiFetch("/api/auth/complete-new-password", {
			method: "POST",
			body: JSON.stringify({ newPassword }),
		});

		const data = await parseJsonOrThrow<{
			success?: boolean;
			error?: string;
			data?: Record<string, unknown>;
		}>(res);

		if (!res.ok || !data.success || !data.data) {
			throw new Error(data?.error ?? "Failed to set new password");
		}

		const user = toUser(data.data);
		try {
			const syncRes = await apiFetch("/api/users/syncs", {
				method: "POST",
				body: JSON.stringify({
					email: user.email,
					name: user.name,
					role: user.role,
				}),
			});
			const syncData = await parseJsonOrThrow<{
				success?: boolean;
				data?: Record<string, unknown>;
			}>(syncRes);
			if (syncRes.ok && syncData.success && syncData.data) {
				return toUser(syncData.data);
			}
		} catch {
			// Sync failed
		}
		return user;
	}

	async logout(): Promise<void> {
		await apiFetch("/api/auth/logout", { method: "POST" });
	}

	async getCurrentUser(): Promise<User | null> {
		try {
			const res = await apiFetch("/api/users/me");
			if (!res.ok) return null;

			const data = await parseJsonOrThrow<{
				success?: boolean;
				data?: Record<string, unknown>;
			}>(res);
			if (data.success && data.data) {
				return toUser(data.data);
			}
		} catch {
			// Not authenticated or network error
		}
		return null;
	}

	// Admin/user management — uses axiosClient which sends credentials
	async signup(email: string, _password: string, name: string): Promise<User> {
		const { default: axiosClient } = await import("../api/axiosClient");
		const data = (await axiosClient.post("/api/users/syncs", {
			email,
			name,
			role: "partner",
		})) as unknown as Record<string, unknown>;
		if (data) return toUser(data);
		throw new Error("Signup failed");
	}

	async createUser(
		email: string,
		password: string,
		name: string,
		role: "admin" | "partner" | "stellar_admin",
	): Promise<User> {
		const { default: axiosClient } = await import("../api/axiosClient");
		const data = (await axiosClient.post("/api/users", {
			email,
			password,
			name,
			role,
		})) as unknown as Record<string, unknown>;
		if (data) return toUser(data);
		throw new Error("Create user failed");
	}

	async updateUser(userId: string, updates: Partial<User>): Promise<User> {
		const { default: axiosClient } = await import("../api/axiosClient");
		const payload: Record<string, unknown> = {};
		if (updates.name) payload.name = updates.name;
		if (updates.role) payload.role = updates.role;
		// REST API expects camelCase
		if (updates.wallet_ids !== undefined)
			payload.walletIds = updates.wallet_ids;
		const data = (await axiosClient.patch(
			`/api/users/${userId}`,
			payload,
		)) as unknown as Record<string, unknown>;
		if (data) return toUser(data);
		throw new Error("Update user failed");
	}

	async getAllUsers(): Promise<User[]> {
		const { default: axiosClient } = await import("../api/axiosClient");
		const data = await axiosClient.get<Record<string, unknown>[]>("/api/users");
		if (Array.isArray(data)) {
			return data.map((u) => toUser(u));
		}
		return [];
	}

	async deleteUser(userId: string): Promise<void> {
		const { default: axiosClient } = await import("../api/axiosClient");
		await axiosClient.delete(`/api/users/${userId}`);
	}

	async resetUserPassword(
		userId: string,
		password: string,
		permanent?: boolean,
	): Promise<void> {
		const { default: axiosClient } = await import("../api/axiosClient");
		await axiosClient.post(`/api/users/${userId}/password-resets`, {
			password,
			permanent,
		});
	}

	async forgotPassword(email: string): Promise<void> {
		const res = await apiFetch("/api/auth/forgot-password", {
			method: "POST",
			body: JSON.stringify({ email }),
		});
		const data = await parseJsonOrThrow<{
			success?: boolean;
			error?: string;
		}>(res);
		if (!res.ok || !data.success) {
			throw new Error(data?.error ?? "Failed to send reset code");
		}
	}

	async confirmForgotPassword(
		email: string,
		code: string,
		newPassword: string,
	): Promise<void> {
		const res = await apiFetch("/api/auth/confirm-forgot-password", {
			method: "POST",
			body: JSON.stringify({ email, code, newPassword }),
		});
		const data = await parseJsonOrThrow<{
			success?: boolean;
			error?: string;
		}>(res);
		if (!res.ok || !data.success) {
			throw new Error(data?.error ?? "Failed to reset password");
		}
	}
}

export const bffAuthService = new BFFAuthService();
